import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import AOS from "aos";
import "aos/dist/aos.css";
import { FaWhatsapp } from "react-icons/fa";
import toast from "react-hot-toast";

const DynamicInquiryForm = () => {
  const indianStates = [
    "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
    "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka",
    "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram",
    "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana",
    "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal", "Andaman and Nicobar Islands",
    "Chandigarh", "Dadra and Nagar Haveli and Daman and Diu", "Delhi", "Jammu and Kashmir",
    "Ladakh", "Lakshadweep", "Puducherry"
  ];

  const initialState = {
    name: "",
    phone: "",
    email: "",
    service: "",
    city: "",
    state: "",
    message: "",
    nameErr: "",
    phoneErr: "",
    emailErr: "",
    serviceErr: "",
    cityErr: "",
    stateErr: "",
    messageErr: "",
    // whatsapp: true,
  };

  const [formState, setFormState] = useState(initialState);
  const [loading, setLoading] = useState(false);
  const [servicesData, setServicesData] = useState([]);
  const [loadingServices, setLoadingServices] = useState(true);
  const form = useRef();

  useEffect(() => {
    AOS.init({ duration: 1000 });
    fetchServices();
  }, []);

  // Fetch services from API
  const fetchServices = async () => {
    try {
      setLoadingServices(true);
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_URL}/services/get-all-services-data`
      );
      
      if (data && Array.isArray(data)) {
        // Sort by order
        const sortedData = [...data].sort((a, b) => a.order - b.order);
        setServicesData(sortedData);
      }
    } catch (error) {
      console.error("Error fetching services:", error);
      toast.error("Failed to load services");
    } finally {
      setLoadingServices(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormState((prevState) => ({
      ...prevState,
      [name]: value,
      [`${name}Err`]: "",
    }));
  };

  const clearState = () => setFormState({ ...initialState });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { name, phone, email, service, state, message } = formState;
    let valid = true;

    // Reset errors
    const errors = {};

    // Validation
    if (!name.trim()) {
      errors.nameErr = "Name is required";
      valid = false;
    }

    if (!phone || !/^[0-9]{10}$/.test(phone)) {
      errors.phoneErr = "Please enter a valid 10-digit phone number";
      valid = false;
    }

    if (!email.trim()) {
      errors.emailErr = "Email is required";
      valid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.emailErr = "Please enter a valid email";
      valid = false;
    }

    if (!service.trim()) {
      errors.serviceErr = "Please select a service";
      valid = false;
    }

    if (!state.trim()) {
      errors.stateErr = "Please select your state";
      valid = false;
    }

    if (!message.trim()) {
      errors.messageErr = "Message is required";
      valid = false;
    }

    if (!valid) {
      setFormState(prev => ({ ...prev, ...errors }));
      return;
    }

    try {
      setLoading(true);

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/inquiry/create-inquiry`,
        { 
          name: name.trim(), 
          email: email.trim().toLowerCase(), 
          phone: phone.trim(), 
          service: service.trim(),
          // city: city.trim(),
          state: state.trim(),
          message: message.trim()
        },
        { headers: { "Content-Type": "application/json" } }
      );

      if (response.status === 200 || response.status === 201) {
        toast.success("Inquiry submitted successfully! We'll get back to you soon.");
        clearState();
      }
    } catch (error) {
      console.error("Error submitting inquiry:", error);
      toast.error(error.response?.data?.message || "Failed to submit inquiry. Please try again!");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-aos="fade-up"
      className="bg-white shadow-lg rounded-2xl overflow-hidden mx-auto"
    >
      {/* Banner */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-500 p-3 text-white font-semibold flex justify-center items-center text-center mx-auto">
        <h2 className="text-center text-xl font-semibold">
          Connect With Us
        </h2>
      </div>

      {/* Form */}
      <div className="p-6">
        <form ref={form} onSubmit={handleSubmit} className="space-y-4">
          {/* Name */}
          <div>
            <input
              type="text"
              className={`w-full border ${formState.nameErr ? 'border-red-500' : 'border-gray-300'} rounded-md p-3 focus:ring-2 focus:ring-orange-500 outline-none`}
              placeholder="Your Name *"
              name="name"
              value={formState.name}
              onChange={handleChange}
            />
            {formState.nameErr && (
              <span className="text-red-500 text-xs block mt-1">{formState.nameErr}</span>
            )}
          </div>

          {/* Email */}
          <div>
            <input
              type="email"
              className={`w-full border ${formState.emailErr ? 'border-red-500' : 'border-gray-300'} rounded-md p-3 focus:ring-2 focus:ring-orange-500 outline-none`}
              placeholder="Email Address *"
              name="email"
              value={formState.email}
              onChange={handleChange}
            />
            {formState.emailErr && (
              <span className="text-red-500 text-xs block mt-1">{formState.emailErr}</span>
            )}
          </div>

          {/* Phone with Country Code */}
          <div>
            <div className={`flex border ${formState.phoneErr ? 'border-red-500' : 'border-gray-300'} rounded-md overflow-hidden`}>
              <span className="flex items-center px-3 bg-gray-100 text-gray-700">
                🇮🇳 +91
              </span>
              <input
                type="tel"
                className="flex-1 p-3 outline-none"
                placeholder="Mobile Number *"
                name="phone"
                maxLength="10"
                value={formState.phone}
                onChange={handleChange}
              />
            </div>
            {formState.phoneErr && (
              <span className="text-red-500 text-xs block mt-1">{formState.phoneErr}</span>
            )}
          </div>

          {/* Service Selection - Dynamic */}
          <div>
            {loadingServices ? (
              <div className="w-full border border-gray-300 rounded-md p-3 bg-gray-50 text-gray-500">
                Loading services...
              </div>
            ) : (
              <select
                name="service"
                value={formState.service}
                onChange={handleChange}
                className={`w-full border ${formState.serviceErr ? 'border-red-500' : 'border-gray-300'} rounded-md p-3 focus:ring-2 focus:ring-orange-500 outline-none`}
              >
                <option value="">--Select Service *--</option>
                {servicesData.map((service) => (
                  <option key={service._id || service.id} value={service.serviceName}>
                    {service.serviceName}
                  </option>
                ))}
              </select>
            )}
            {formState.serviceErr && (
              <span className="text-red-500 text-xs block mt-1">{formState.serviceErr}</span>
            )}
          </div>

          {/* City */}
          {/* <div>
            <input
              type="text"
              className="w-full border border-gray-300 rounded-md p-3 focus:ring-2 focus:ring-orange-500 outline-none"
              placeholder="City (Optional)"
              name="city"
              value={formState.city}
              onChange={handleChange}
            />
          </div> */}

          {/* State */}
          <div>
            <select
              name="state"
              value={formState.state}
              onChange={handleChange}
              className={`w-full border ${formState.stateErr ? 'border-red-500' : 'border-gray-300'} rounded-md p-3 focus:ring-2 focus:ring-orange-500 outline-none`}
            >
              <option value="">--Select State *--</option>
              {indianStates.map((s, i) => (
                <option key={i} value={s}>
                  {s}
                </option>
              ))}
            </select>
            {formState.stateErr && (
              <span className="text-red-500 text-xs block mt-1">{formState.stateErr}</span>
            )}
          </div>

          {/* Message */}
          <div>
            <textarea
              className={`w-full border ${formState.messageErr ? 'border-red-500' : 'border-gray-300'} rounded-md p-3 focus:ring-2 focus:ring-orange-500 outline-none resize-none`}
              placeholder="Enter your Message *"
              name="message"
              rows="4"
              value={formState.message}
              onChange={handleChange}
            />
            {formState.messageErr && (
              <span className="text-red-500 text-xs block mt-1">{formState.messageErr}</span>
            )}
          </div>

          {/* WhatsApp Toggle (Optional) */}
          {/* <div className="flex items-center justify-between bg-gray-50 p-3 rounded-md">
            <label className="flex items-center gap-2 text-gray-700 text-sm">
              <FaWhatsapp className="text-green-500 text-xl" />
              Get Updates on WhatsApp
            </label>
            <button
              type="button"
              onClick={() =>
                setFormState((prev) => ({ ...prev, whatsapp: !prev.whatsapp }))
              }
              className={`w-12 h-6 flex items-center rounded-full p-1 transition ${
                formState.whatsapp ? "bg-green-500" : "bg-gray-300"
              }`}
            >
              <div
                className={`bg-white w-4 h-4 rounded-full shadow-md transform transition ${
                  formState.whatsapp ? "translate-x-6" : "translate-x-0"
                }`}
              ></div>
            </button>
          </div> */}

          {/* Submit Button */}
          <button
            disabled={loading || loadingServices}
            type="submit"
            className={`w-full bg-orange-500 hover:bg-orange-600 text-white py-3 rounded-md font-bold text-lg transition-all ${
              (loading || loadingServices) ? 'opacity-70 cursor-not-allowed' : ''
            }`}
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Submitting...
              </span>
            ) : (
              'Submit Inquiry'
            )}
          </button>
        </form>

        {/* Required Fields Note */}
        <p className="text-xs text-gray-500 text-center mt-4">
          * Required fields
        </p>
      </div>
    </div>
  );
};

export default DynamicInquiryForm;